import * as t from 'io-ts';

import {colorForInteger} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {FrameTypesEnum, makeEnumRuntimeType, RApplicationIdRef} from './apiTypes';
import {
  parseApplicationEntryPointAction,
  RApplicationEntryPointAction,
} from './applicationEntryPointActionModel';

/*
 * Constants.
 */

export enum ApplicationEntryPointTypesEnum {
  INBOX_SIDE_PANEL = 'inbox_side_panel',
}

export const RApplicationEntryPointTypesEnum = makeEnumRuntimeType(ApplicationEntryPointTypesEnum);

/*
 * Runtime type.
 */

export const RBaseApplicationEntryPointDirty = t.readonly(
  t.type({
    type: RApplicationEntryPointTypesEnum,
    action: t.union([RApplicationEntryPointAction, t.undefined]),
  }),
);

const RGenericApplicationEntryPointDirty = t.intersection([
  RBaseApplicationEntryPointDirty,
  t.type({
    applicationId: RApplicationIdRef,
    name: t.string,
  }),
]);

const RApplicationInboxSidePanelEntryPointDirty = t.intersection([
  RGenericApplicationEntryPointDirty,
  t.type({
    frameId: t.string,
    frameType: t.literal(FrameTypesEnum.APPLICATION_ENTRY_POINT),
    type: t.literal(ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL),
    color: makeEnumRuntimeType(PaletteColorsEnum),
  }),
]);

// This will be a union once we begin supporting more entry point types.
export const RApplicationEntryPointDirty = RApplicationInboxSidePanelEntryPointDirty;

/*
 * Type.
 */

export interface ApplicationEntryPointModel extends t.TypeOf<typeof RApplicationEntryPointDirty> {}
export const RApplicationEntryPoint = t.clean<ApplicationEntryPointModel>(RApplicationEntryPointDirty as any);

/*
 * IO methods.
 */

export function parseApplicationEntryPoints(src: any): ReadonlyArray<ApplicationEntryPointModel> {
  return (
    src.application &&
    src.application.entry_points &&
    src.application.entry_points.map((entryPoint: any) => parseApplicationEntryPoint(entryPoint, src))
  );
}

export function parseApplicationEntryPoint(
  entryPoint: any,
  src: any,
): ApplicationEntryPointModel | undefined {
  if (!RApplicationEntryPointTypesEnum.is(entryPoint.type)) {
    return undefined;
  }
  if (entryPoint.type === ApplicationEntryPointTypesEnum.INBOX_SIDE_PANEL) {
    return {
      type: entryPoint.type,
      action: parseApplicationEntryPointAction(entryPoint.action),
      name: src.application?.name || '',
      applicationId: src.id,
      frameId: `entryPoint:${src.id}`,
      frameType: FrameTypesEnum.APPLICATION_ENTRY_POINT,
      color: colorForInteger(src.id),
    };
  }
  return undefined;
}
